import { z } from 'zod'

/**
 * The typed values facts are made of, which the page-tree resolver formats by type.
 *
 * Scalars are branded primitives: the value stays plain JSON, and the kind lives in the
 * schema — a fact schema reuses these exports, so a variable's kind is read from the
 * schema its path walks to, never guessed from the value (a count and a duration are both
 * numbers).
 */

/** An ISO 4217 code. Not `DisplayCurrency`: a fixed amount keeps whatever currency it
 *  was quoted in, including one nexus never displays. */
const CurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/)

/** `fixed` marks an amount that stays in its own currency whatever the page renders in
 *  (a local transit fare); an unfixed amount is canonical and converts. */
export const MoneySchema = z.strictObject({
    amount: z.number(),
    currency: CurrencyCodeSchema,
    fixed: z.boolean().optional(),
})
export type Money = z.infer<typeof MoneySchema>

export const CountSchema = z.number().int().nonnegative().brand<'Count'>()
export type Count = z.infer<typeof CountSchema>

/** Minutes. */
export const DurationSchema = z.number().nonnegative().brand<'Duration'>()
export type Duration = z.infer<typeof DurationSchema>

/** Kilometres — the one canonical distance; the formatter states it in the locale's unit. */
export const DistanceSchema = z.number().nonnegative().brand<'Distance'>()
export type Distance = z.infer<typeof DistanceSchema>

/** A review rating on the review form's 1–5 scale. */
export const RatingSchema = z.number().min(0).max(5).brand<'Rating'>()
export type Rating = z.infer<typeof RatingSchema>

/** 1-7, Monday-Sunday — the index `weekdayName` (`@/i18n/calendar`) reads. */
export const WeekdaySchema = z.number().int().min(1).max(7).brand<'Weekday'>()
export type Weekday = z.infer<typeof WeekdaySchema>

/** An instant, so it renders in the page type's display time zone rather than the
 *  server's (→ invariants.md#ssr-safe-rendered-dates). */
export const InstantSchema = z.iso.datetime({ offset: true }).brand<'Instant'>()
export type Instant = z.infer<typeof InstantSchema>

const isHref = (value: string): boolean =>
    !/\s/.test(value) &&
    ((value.startsWith('/') && !value.startsWith('//')) || /^https?:\/\/[^/]/.test(value))

/** A link destination as a whole value — an internal one built by the i18n navigation
 *  helpers, so it is canonical by construction (→ invariants.md#rendered-links-are-canonical). */
export const HrefSchema = z
    .string()
    .refine(isHref, 'not a root-relative path or http(s) URL')
    .brand<'Href'>()
export type Href = z.infer<typeof HrefSchema>

/** A place by its canonical name and ids. Its display name is editorial and comes from
 *  the store (→ invariants.md#facts-canonical-store-editorial). */
export const PlaceSchema = z.strictObject({
    wcityid: z.string().min(1),
    name: z.string().min(1),
    placeId: z.string().min(1),
    countryCode: z.string().min(1),
    stateCode: z.string().min(1).optional(),
})
export type Place = z.infer<typeof PlaceSchema>

/** Every kind a variable or a declared derivation result can have, and its schema. */
export const VALUE_SCHEMAS = {
    text: z.string(),
    count: CountSchema,
    duration: DurationSchema,
    distance: DistanceSchema,
    rating: RatingSchema,
    weekday: WeekdaySchema,
    instant: InstantSchema,
    money: MoneySchema,
    href: HrefSchema,
} as const
export type ValueKind = keyof typeof VALUE_SCHEMAS
export type ValueOf<K extends ValueKind> = z.infer<(typeof VALUE_SCHEMAS)[K]>
